if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.variantConfig = JSON.parse(this.querySelector('[data-sesseq-variant-groups]')?.textContent || '{}');
        this.setVariantMedia(this.dataset.variantId);
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.hasAttribute('data-sesseq-carousel')) {
          this.elements.viewer.querySelector('.product__media-list')?.addEventListener('keydown', (event) => {
            if (event.target !== event.currentTarget) return;
            const track = event.currentTarget;
            const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
            const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              event.preventDefault();
              track.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * track.clientWidth, behavior });
            } else if (event.key === 'Home' || event.key === 'End') {
              event.preventDefault();
              track.scrollTo({ left: event.key === 'Home' ? 0 : direction * (track.scrollWidth - track.clientWidth), behavior });
            }
          });
        }
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (!this.hasAttribute('data-sesseq-carousel') && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setVariantMedia(variantId) {
        const config = this.variantConfig || {};
        const heads = Object.values(config.variants || {}).filter(Boolean).map(String);
        let allowed = config.groups?.[String(variantId)];
        if (!allowed?.length && new Set(heads).size > 1 && config.variants?.[variantId]) {
          allowed = [String(config.variants[variantId])];
        }
        const ids = allowed?.length ? new Set(allowed.map(String)) : null;
        const modal = document.getElementById(`ProductModal-${this.dataset.sectionId}`);
        [this, modal].filter(Boolean).forEach(root => {
          root.querySelectorAll('[data-sesseq-media-id]').forEach(item => {
            const hide = !!ids && !ids.has(item.dataset.sesseqMediaId);
            item.toggleAttribute('data-sesseq-variant-hidden', hide);
            if (hide) item.classList.remove('is-active', 'active');
          });
        });
        const count = this.elements.viewer.querySelectorAll('[data-sesseq-media-id]:not([data-sesseq-variant-hidden])').length;
        [this.elements.viewer, this.elements.thumbnails].filter(Boolean).forEach(slider => {
          slider.resetPages?.();
          const controls = slider.querySelector('.slider-buttons');
          if (controls) controls.toggleAttribute('data-sesseq-variant-hidden', count < 2);
        });
        return allowed?.[0];
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        if (this.hasAttribute('data-sesseq-carousel')) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
            item.classList.toggle('is-active', item === event.detail.currentElement);
          });
          this.playActiveMedia(event.detail.currentElement);
          this.announceLiveRegion(event.detail.currentElement, event.detail.currentPage);
        }
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia || activeMedia.hasAttribute('data-sesseq-variant-hidden')) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.hasAttribute('data-sesseq-carousel')) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'instant' });
          } else if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.hasAttribute('data-sesseq-carousel') && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}


